import {IPageResponse} from "../../common/interfaces/IPageResponse";
import {ModalType} from "../../common/common-models";
import {IPageRequest} from "../../common/interfaces/IPageRequest";
import { ContractPeriodType } from "components/ChargingAgreements/models";

export interface IElectricityPackagesState {
    electricityPackages: IPageResponse<IElectricityPackage> | null;
    pageRequest: IPageRequest<IElectricityPackageSortFields>;
    isEditModalVisible: boolean;
    isDeleteModalVisible: boolean;
    modalType: ModalType;
    electricityPackage?: IElectricityPackage;
}

export interface IElectricityPackage {
    id: number;
    publicId: number;
    nameEt: string;
    nameEn: string;
    descriptionEt: string;
    descriptionEn: string;
    marketPricePackage: boolean;
    pdfProductDescriptionOnInvoice?: string;
    externalProductDescriptionOnInvoice?: string;
    jointInvoicePackage: boolean;
    dynamicPackage: boolean;
    baseFixationType?: ElectricityPackageBaseFixationType;
    baseConsumptionProportion?: number;
    twoTariffPackage: boolean;
    dayTariffCentsKwh?: number;
    nightTariffCentsKwh?: number;
    basePriceCentsKwh: number;
    basePriceTextEt: string;
    basePriceTextEn: string;
    basePriceDescriptionEt: string;
    basePriceDescriptionEn: string;
    monthlyFeeEuros?: number;
    earlyTerminationFeeType?: ElectricityPackageEarlyTerminationFeeType;
    earlyTerminationFee?: number;
    contractPeriod?: ContractPeriod;
    contractEndDate?: string;
    greenEnergy: boolean;
    marginals: Array<IElectricityPackageMarginal>;
    active: boolean;
    highlighted: boolean;
    orderNumber: number;
    contractTitle?: string;
    endDate?: string;
    pdfFooterDisclaimerPerson?: string;
    pdfFooterDisclaimerCompany?: string;
    dateModified: string;
    type: ElectricityPackageType;
    meterType: ElectricityPackageMeterType;
    showToPrivateCustomer: boolean;
    showToBusinessCustomer: boolean;
    terms: { id: number, name: string};
}

export interface IElectricityPackageWithMarginal {
    id: number;
    publicId: number;
    nameEt: string;
    nameEn: string;
    descriptionEt: string;
    descriptionEn: string;
    marketPricePackage: boolean;
    jointInvoicePackage: boolean;
    dynamicPackage: boolean;
    twoTariffPackage: boolean;
    dayTariffCentsKwh?: number;
    nightTariffCentsKwh?: number;
    baseFixationType?: ElectricityPackageBaseFixationType;
    baseConsumptionProportion?: number;
    termsAndConditions: string;
    basePriceCentsKwh?: number;
    basePriceTextEt?: string;
    basePriceTextEn?: string;
    basePriceDescriptionEt?: string;
    basePriceDescriptionEn?: string;
    fixedPrice: boolean;
    contractPeriod?: ContractPeriod;
    contractEndDate?: string;
    greenEnergy: boolean;
    marginal: number;
    averageMarketPrice?: number;
    monthlyFeeEuros?: number;
    highlighted: boolean;
    orderNumber?: number;
    confirmationText?: string;
    loyaltyConfirmationText?: string;
    agreementWithdrawalConditions?: string;
    type: ElectricityPackageType;
    meterType: ElectricityPackageMeterType;
    showToPrivateCustomer: boolean;
    showToBusinessCustomer: boolean;
}

export interface ContractPeriod {
    id: number;
    name: string;
    type: ContractPeriodType;
}

export enum ElectricityPackageType {
    PRODUCTION = 'PRODUCTION',
    CONSUMPTION = 'CONSUMPTION'
}

export const ELECTRICITY_PACKAGE_TYPE_MAP: Record<ElectricityPackageType, string> = {
    PRODUCTION: 'Tootmine',
    CONSUMPTION: 'Tarbimine',
};

export enum ElectricityPackageBaseFixationType {
    QUANTITY = 'QUANTITY',
    PERCENTAGE = 'PERCENTAGE'
}

export const ELECTRICITY_PACKAGE_BASE_FIXATION_TYPE_MAP: Record<ElectricityPackageBaseFixationType, string> = {
    QUANTITY: 'Baastarbimine',
    PERCENTAGE: 'Protsent tarbimisest',
};

export const ELECTRICITY_PACKAGE_BASE_FIXATION_TYPES = [
  { value: ElectricityPackageBaseFixationType.QUANTITY, label: ELECTRICITY_PACKAGE_BASE_FIXATION_TYPE_MAP[ElectricityPackageBaseFixationType.QUANTITY] },
  { value: ElectricityPackageBaseFixationType.PERCENTAGE, label: ELECTRICITY_PACKAGE_BASE_FIXATION_TYPE_MAP[ElectricityPackageBaseFixationType.PERCENTAGE] },
];

export enum ElectricityPackageMeterType {
    QUARTER_HOUR = 'QUARTER_HOUR',
    HOUR = 'HOUR'
}

export const ELECTRICITY_PACKAGE_METER_TYPE_MAP: Record<ElectricityPackageMeterType, string> = {
    QUARTER_HOUR: '15-minuti arvestus',
    HOUR: 'Tunniarvestus',
};

export const ELECTRICITY_PACKAGE_METER_TYPES = [
  { value: ElectricityPackageMeterType.QUARTER_HOUR, label: ELECTRICITY_PACKAGE_METER_TYPE_MAP[ElectricityPackageMeterType.QUARTER_HOUR] },
  { value: ElectricityPackageMeterType.HOUR, label: ELECTRICITY_PACKAGE_METER_TYPE_MAP[ElectricityPackageMeterType.HOUR] },
];

export enum ElectricityPackageEarlyTerminationFeeType {
    FIXED_PER_LEFTOVER_MONTH = 'FIXED_PER_LEFTOVER_MONTH',
    PERCENTAGE_OF_VOLUME = 'PERCENTAGE_OF_VOLUME'
}

export const ELECTRICITY_PACKAGE_EARLY_TERMINATION_FEE_TYPE_MAP: Record<ElectricityPackageEarlyTerminationFeeType, string> = {
    FIXED_PER_LEFTOVER_MONTH: 'Tasu lepingu lõpuni jäänud kuud eest',
    PERCENTAGE_OF_VOLUME: 'Protsent lepingu lõpuni jäänud arvestuslikust tarbimismahust',
};

export const ELECTRICITY_PACKAGE_EARLY_TERMINATION_FEE_TYPES = [
  { value: ElectricityPackageEarlyTerminationFeeType.FIXED_PER_LEFTOVER_MONTH, label: ELECTRICITY_PACKAGE_EARLY_TERMINATION_FEE_TYPE_MAP[ElectricityPackageEarlyTerminationFeeType.FIXED_PER_LEFTOVER_MONTH] },
  { value: ElectricityPackageEarlyTerminationFeeType.PERCENTAGE_OF_VOLUME, label: ELECTRICITY_PACKAGE_EARLY_TERMINATION_FEE_TYPE_MAP[ElectricityPackageEarlyTerminationFeeType.PERCENTAGE_OF_VOLUME] },
];

export interface IElectricPackageTableProps {
    electricityPackages: IPageResponse<IElectricityPackage>;
    openElectricityPackageModal: (modalType: ModalType, electricityPackage?: IElectricityPackage) => void;
    openDeleteElectricityPackageModal: (electricityPackage: IElectricityPackage) => void;
    onPageChange: (page: number, size: number) => void;
}

export interface IElectricityPackageMarginal {
    id: number;
    packageID: number;
    price: number;
    maxValue: number;
    dateCreated: string;
}

export interface IElectricityPackageFormMarginal {
    id?: number;
    price?: number;
    maxValue?: number;
}

export enum IElectricityPackageSortFields {
    ID = "ID",
    PUBLIC_ID = "PUBLIC_ID",
    DATE_MODIFIED = "DATE_MODIFIED"
}

export interface IElectricityPackagesSearchBarProps {
    openElectricityPackageModal: (modalType: ModalType, electricityPackage?: IElectricityPackage) => void;
}

export interface IElectricityPackageModalProps {
    modalType: ModalType;
    handleModalHide: () => void;
    editingElectricityPackage?: IElectricityPackage;
    loadElectricityPackages: () => void;
}

export interface IElectricityPackageForm {
    nameEt: string;
    nameEn?: string;
    descriptionEt: string;
    descriptionEn?: string;
    marketPricePackage?: boolean;
    meterType?: ElectricityPackageMeterType;
    jointInvoicePackage?: boolean;
    dynamicPackage: boolean;
    baseFixationType?: ElectricityPackageBaseFixationType;
    baseConsumptionProportion?: number;
    twoTariffPackage: boolean;
    dayTariffCentsKwh?: number;
    nightTariffCentsKwh?: number;
    basePriceCentsKwh?: number;
    basePriceTextEt?: string;
    basePriceTextEn?: string;
    basePriceDescriptionEt: string;
    basePriceDescriptionEn?: string;
    monthlyFeeEuros?: number;
    earlyTerminationFeeType?: ElectricityPackageEarlyTerminationFeeType;
    earlyTerminationFee?: number;
    contractPeriodId?: number;
    contractEndDate?: string;
    greenEnergy: boolean;
    active: boolean;
    highlighted: boolean;
    orderNumber?: number;
    contractTitle?: string;
    pdfFooterDisclaimerPerson?: string;
    pdfFooterDisclaimerCompany?: string;
    termsAndConditions?: { value: number, label: string };
    termsAndConditionsId?: number;
    showToPrivateCustomer: boolean;
    showToBusinessCustomer: boolean;
    marginals: Array<IElectricityPackageFormMarginal>;
    pdfProductDescriptionOnInvoice?: string;
    externalProductDescriptionOnInvoice?: string;
}